import {
  Avatar,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "styled-components";
import { CustomButton, StyledDivider } from "../../globalStyle";
import { ButtonText, HeaderSubTitle } from "../../pageComponents/style";
import { CardSubTitle } from "../dashboardSections/style";
import { useDispatch, useSelector } from "react-redux";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import {
  deleteTeamLoaderSelector,
  teamDetailsSelector,
} from "../../store/metadata/selector";
import { teamMemberDetail } from "../../store/metadata/types";
import { useState } from "react";
import { userDetailsSelector } from "../../store/account/selector";
import { EditIcon } from "../../assets/Icons";
import EditTeamModal from "../modals/EditTeamModal";
import DeleteConfirmationModal from "../modals/deleteConfirmationModal";

const RenderButton = ({
  background,
  color,
  borderColor,
  children,
}: {
  background: string;
  color: string;
  borderColor: string;
  children: React.ReactNode;
}) => {
  return (
    <CustomButton
      sx={{
        padding: "0.625rem 1.25rem",
        borderRadius: "1.25rem",
        background: background,
        border: `1px solid ${borderColor}`,
        color: color,
        pointerEvents: "none",
      }}
    >
      {children}
    </CustomButton>
  );
};

export const TeamSettingsForm = () => {
  // Selectors
  const theme = useTheme();
  const userCurrentTeamDetail = useSelector(userDetailsSelector);
  const teamMembers = useSelector(teamDetailsSelector);
  const dispatch = useDispatch();
  const removeTeamloading = useSelector(deleteTeamLoaderSelector);
  const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);

  const handleDeleteTeam = () => {
    dispatch(MetaDataSagaActions.deleteTeam(teamMembers[0]?._id));
    setIsOpenDeleteModal(false);
  };

  const handleShowDeleteModal = () => {
    setIsOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  const isShowEditButton =
    teamMembers?.length &&
    teamMembers[0]?.members?.find((member: any) => member.isOwner === true)
      ?.email === userCurrentTeamDetail?.email;

  return (
    <Stack marginTop={"2.5rem"}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <HeaderSubTitle>Team Members</HeaderSubTitle>
        {isShowEditButton ? (
          <CustomButton
            variant="contained"
            size="small"
            sx={{
              padding: "0.625rem 1.25rem",
              fontSize: "1rem",
              fontWeight: "500",
              borderRadius: "1.786rem !important",
              color: "white !important",
              background: `${theme.colors.backgroundPrimary} !important`,
              height: "2.6rem",
              cursor: "pointer",
            }}
            onClick={() => setIsOpenEditModal(true)}
          >
            <EditIcon />
            <ButtonText>Edit Team</ButtonText>
          </CustomButton>
        ) : null}
      </Box>
      <StyledDivider sx={{ margin: "10px 0px" }} />
      <Box
        sx={{
          width: "100%",
          maxHeight: "30rem",
          padding: "0.625rem",
          borderRadius: "0.625rem",
          overflowY: "auto",
          overflowX: "hidden",
          "::-webkit-scrollbar": {
            width: "4px",
          },
        }}
      >
        {Array.isArray(teamMembers) && teamMembers?.length > 0
          ? teamMembers[0]?.members?.map(
              (team: teamMemberDetail, index: number) => {
                return (
                  <Box
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      key={index}
                      display={"flex"}
                      alignItems={"center"}
                      gap="0.75rem"
                      width={"49%"}
                      marginBottom={"1.875rem"}
                    >
                      <Avatar
                        src={
                          team?.profileImage
                            ? team.profileImage
                            : "assests/images/user-logo.png"
                        }
                        sx={{
                          width: "2.5rem",
                          height: "2.5rem",
                        }}
                      />
                      <Stack gap={"0.25rem"} marginRight="3.75rem" width="50%">
                        <Typography
                          fontSize="1rem"
                          fontWeight="500"
                          lineHeight="1.219rem"
                          color={theme.colors.black2}
                        >
                          {team.fullName ? team.fullName : "Guest User"}
                        </Typography>
                        <CardSubTitle>{team.email}</CardSubTitle>
                      </Stack>
                      {team.accepted ? (
                        <RenderButton
                          background={theme.colors.mintCream}
                          borderColor={theme.colors.cyan}
                          color={theme.colors.green1}
                          children={team?.isOwner ? "Owner" : "Member"}
                        />
                      ) : (
                        <RenderButton
                          background={theme.colors.lavenderBlush}
                          borderColor={theme.colors.mistyRose}
                          color={theme.colors.red200}
                          children="Pending"
                        />
                      )}
                    </Box>
                    {teamMembers[0]?.canDelete &&
                      (!team?.isOwner ? null : (
                        <CustomButton
                          onClick={handleShowDeleteModal}
                          disabled={removeTeamloading}
                          startIcon={
                            removeTeamloading && (
                              <CircularProgress size={20} color="inherit" />
                            )
                          }
                          sx={{
                            padding: "10px 20px",
                            height: "2.25rem",
                            borderRadius: "20px",
                            border: "1px solid #EAECEE",
                            color: "#414042",
                            fontWeight: 500,
                          }}
                        >
                          Delete Team
                        </CustomButton>
                      ))}
                  </Box>
                );
              }
            )
          : null}
      </Box>
      <EditTeamModal
        isOpen={isOpenEditModal}
        teamId={userCurrentTeamDetail?.currentTeamId}
        setIsOpen={setIsOpenEditModal}
      />
      <DeleteConfirmationModal
        isOpen={isOpenDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDeleteTeam}
        modalTitle="Delete Team"
        modalDescription="Are you sure you want to delete this team? This action cannot be undone."
        loading={removeTeamloading}
        btnLabel="confirm"
      />
    </Stack>
  );
};
